import { ReactNode, createContext, useContext, useMemo, useState } from "react";
import { SORT_DIRECTION } from "hooks/useSort";
import isEqual from "lodash/isEqual";

export type FilterStateValue = string[] | boolean | Date | undefined | string;

export type ActiveSort<T extends Record<string, any> = any> = {
  key: keyof T & string;
  direction: SORT_DIRECTION & string;
};

type RequestParamsState = {
  searchTerm: string;
  onChangeSearchTerm(searchTerm: string): void;
  hasSearchTerm: boolean;
  clearSearchTerm(): void;

  activeSort: ActiveSort | undefined;
  onChangeSort(sortKey?: string, direction?: SORT_DIRECTION): void;

  onChangeToggle(toggle: string, value: boolean): void;
  onChangeSelect(key: string, options: string[] | undefined): void;
  onChangeDateRange(
    key: string,
    start: Date | undefined,
    end: Date | undefined
  ): void;
  onChangeString(key: string, value: string | undefined): void;

  filters: Record<string, FilterStateValue>;
  isUsingInitialFilters: boolean;
  hasSelectedFilters: boolean;
  clearFilters(): void;
  resetFilters(): void;
};

const RequestParamsContext = createContext<RequestParamsState>(
  {} as RequestParamsState
);

const removeEmptyArrays = (filters: Record<string, FilterStateValue>) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, filterValue]) =>
      Array.isArray(filterValue) ? filterValue.length > 0 : filterValue
    )
  );

const compareFilters = (
  filtersA: Record<string, FilterStateValue>,
  filtersB: Record<string, FilterStateValue>
) => isEqual(removeEmptyArrays(filtersA), removeEmptyArrays(filtersB));

const RequestParamsProvider = ({
  children,
  initialFilters = {},
  initialSort,
}: {
  children: ReactNode;
  initialFilters?: Record<string, FilterStateValue>;
  initialSort?: ActiveSort;
}): React.ReactElement => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeSort, setActiveSort] = useState<ActiveSort | undefined>(
    initialSort
  );
  const [filters, setFilters] =
    useState<Record<string, FilterStateValue>>(initialFilters);

  const hasSelectedFilters = useMemo(
    () => !compareFilters(filters, {}),
    [filters]
  );

  const isUsingInitialFilters = useMemo(
    () => compareFilters(initialFilters, filters),
    [filters, initialFilters]
  );

  const onChangeSort = useMemo(
    () => (key: string, direction: SORT_DIRECTION) =>
      setActiveSort(key && direction ? { key, direction } : undefined),
    []
  );

  const onChangeToggle = useMemo(
    () => (filterKey: string, selected: FilterStateValue) =>
      setFilters((currentFilters) => ({
        ...currentFilters,
        [filterKey]: selected,
      })),
    []
  );

  const onChangeDateRange = useMemo(
    () => (filterKey: string, start: Date | undefined, end: Date | undefined) =>
      setFilters((currentFilters) => ({
        ...currentFilters,
        [`${filterKey}StartDate`]: start,
        [`${filterKey}EndDate`]: end,
      })),
    []
  );

  const onChangeSelect = useMemo(
    () => (key: string, options: string[] | undefined) =>
      setFilters((currentFilters) => ({ ...currentFilters, [key]: options })),
    []
  );

  const onChangeString = useMemo(
    () => (key: string, value: string | undefined) =>
      setFilters((currentFilters) => ({ ...currentFilters, [key]: value })),
    []
  );

  const value = useMemo<RequestParamsState>(
    () => ({
      searchTerm,
      onChangeSearchTerm: setSearchTerm,
      hasSearchTerm: !!searchTerm,
      clearSearchTerm: () => setSearchTerm(""),

      activeSort,
      onChangeSort,

      onChangeToggle,
      onChangeDateRange,
      onChangeSelect,
      onChangeString,

      filters,
      isUsingInitialFilters,
      hasSelectedFilters,
      clearFilters: () => setFilters({}),
      resetFilters: () => setFilters(initialFilters ?? {}),
    }),
    [
      searchTerm,
      activeSort,
      onChangeSort,
      onChangeToggle,
      onChangeDateRange,
      onChangeSelect,
      onChangeString,
      filters,
      isUsingInitialFilters,
      hasSelectedFilters,
      initialFilters,
    ]
  );

  return (
    <RequestParamsContext.Provider value={value}>
      {children}
    </RequestParamsContext.Provider>
  );
};

export const useRequestParams = (): RequestParamsState => {
  const requestParams = useContext(RequestParamsContext);
  if (!requestParams) {
    throw new Error("Request Parameters context not initialized");
  }

  return requestParams;
};

export default RequestParamsProvider;
